import React from 'react';
import { motion } from 'framer-motion';
import { Wifi, WifiOff, Smartphone, Zap, CheckCircle2, ArrowRight } from 'lucide-react';

export const ContractorSection = () => {
  return (
    <section className="py-24 md:py-32 relative overflow-hidden bg-[#020408]">
      <div className="w-full max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 text-orange-500 font-mono text-xs uppercase tracking-widest mb-4">
               <Smartphone size={14} />
               <span>For Contractors</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              The Bolt Platform. <br />
              <span className="text-slate-400">Zero friction field ops.</span>
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8 font-light">
              Stop fighting with clunky legacy software. Bolt is built for the foreman in the lift, not the admin in the trailer. It works offline, syncs instantly, and automates the paperwork so your crews can build.
            </p>

            <div className="space-y-6 mb-8">
              {[
                { 
                    title: 'Offline-First Architecture', 
                    desc: 'Works perfectly in basements and tunnels. Syncs automatically when signal returns.' 
                },
                { 
                    title: '1-Tap Permits', 
                    desc: 'Hot work, confined space, and lift plans generated in seconds with context-aware pre-fills.' 
                },
                { 
                    title: 'Automated Toolbox Talks', 
                    desc: 'Daily briefings pushed to every worker device, verified with biometric sign-off.' 
                }
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                   <div className="mt-1 flex-shrink-0 w-8 h-8 rounded-full bg-orange-500/10 flex items-center justify-center border border-orange-500/20 text-orange-400">
                      <CheckCircle2 size={16} />
                   </div>
                   <div>
                      <h4 className="text-white font-bold text-sm mb-1">{item.title}</h4>
                      <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                   </div>
                </div>
              ))}
            </div>

            <button className="flex items-center gap-2 text-orange-400 font-bold hover:gap-3 transition-all group">
                Explore Bolt Features <ArrowRight size={18} />
            </button>
          </motion.div>

          {/* Visual: The Sync Engine */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative h-[500px] bg-[#0F141F] rounded-3xl border border-white/10 overflow-hidden shadow-2xl flex items-center justify-center"
          >
              {/* Background */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-orange-500/10 via-[#0F141F] to-[#0F141F]" />
              
              {/* Central Sync Hub */}
              <div className="relative z-10 w-full max-w-sm">
                  {/* Phone Node */}
                  <motion.div 
                     initial={{ y: 0 }}
                     animate={{ y: [0, -10, 0] }}
                     transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                     className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-[#1A2235] p-6 rounded-2xl border border-orange-500/30 shadow-xl z-20"
                  >
                      <div className="flex items-center gap-3 mb-3">
                          <WifiOff className="text-slate-500" size={20} />
                          <span className="text-xs font-bold text-slate-400 uppercase">Offline Mode</span>
                      </div>
                      <div className="space-y-2">
                          <div className="h-2 w-24 bg-slate-700 rounded-full" />
                          <div className="h-2 w-16 bg-slate-700 rounded-full" />
                      </div>
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 border-[#1A2235]">3</div>
                  </motion.div>

                  {/* Cloud Node */}
                  <motion.div 
                     initial={{ y: 0 }}
                     animate={{ y: [0, 10, 0] }}
                     transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                     className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-[#1A2235] p-6 rounded-2xl border border-blue-500/30 shadow-xl z-20"
                  >
                      <div className="flex items-center gap-3 mb-3">
                          <Wifi className="text-blue-400" size={20} />
                          <span className="text-xs font-bold text-blue-400 uppercase">Cloud Sync</span>
                      </div>
                      <div className="space-y-2">
                          <div className="h-2 w-24 bg-blue-500/20 rounded-full" />
                          <div className="h-2 w-16 bg-blue-500/20 rounded-full" />
                      </div>
                  </motion.div>

                  {/* Data Particles */}
                  <div className="absolute inset-0 flex items-center justify-center">
                      <svg className="w-full h-20 overflow-visible">
                          <motion.path 
                             d="M 50 40 L 300 40"
                             fill="none"
                             stroke="url(#gradient-line)"
                             strokeWidth="2"
                             strokeDasharray="5 5"
                          />
                          <defs>
                              <linearGradient id="gradient-line" x1="0" y1="0" x2="1" y2="0">
                                  <stop offset="0%" stopColor="#f97316" stopOpacity="0.5" />
                                  <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.5" />
                              </linearGradient>
                          </defs>
                          
                          {/* Traveling Packets */}
                          {[0, 1, 2].map(i => (
                              <motion.circle
                                key={i}
                                r="4"
                                fill="#fff"
                                initial={{ cx: 50, cy: 40, opacity: 0 }}
                                animate={{ cx: 300, cy: 40, opacity: [0, 1, 0] }}
                                transition={{ 
                                    duration: 2, 
                                    repeat: Infinity, 
                                    delay: i * 0.6,
                                    ease: "easeInOut"
                                }}
                              />
                          ))}
                      </svg>
                  </div>

              </div>

          </motion.div>

        </div>
      </div>
    </section>
  );
};






